import { LegalLayout, LegalSection, LegalList } from "@/components/LegalLayout";

export default function TermsOfServicePage() {
  return (
    <LegalLayout
      testId="terms-of-service-page"
      eyebrow="Legal"
      title="Terms of Service"
      subtitle="The terms that apply when you use the Coffice 24x7 website, centers, and memberships."
      lastUpdated="18 August 2026"
    >
      <LegalSection title="1. Acceptance of Terms">
        <p>
          These Terms of Service govern your use of the coffice24x7.in website and of any
          workspace, day pass, meeting room, or membership provided by Coffice 24x7
          ("Coffice", "we", "us", or "our"). By using the website or entering one of our
          centers, you agree to these terms.
        </p>
        <p>
          If you book on behalf of a company or team, you confirm that you are authorised to
          accept these terms for that organisation.
        </p>
      </LegalSection>

      <LegalSection title="2. Memberships and Bookings">
        <LegalList
          items={[
            "Memberships are personal to the named member and may not be shared or transferred without our written consent.",
            "All plans, seat types, and prices listed on the website are indicative and subject to availability at the chosen center.",
            "A booking is confirmed only once we acknowledge it in writing and the applicable payment has been received.",
            "Meeting rooms and other bookable resources are allotted on a first-come, first-served basis and may carry separate charges.",
          ]}
        />
      </LegalSection>

      <LegalSection title="3. Fees, Payment, and Renewal">
        <p>
          Membership fees are payable in advance for each billing period. Taxes apply as per
          Indian law. A refundable security deposit may be required for dedicated desks and
          private cabins, and is returned after adjusting any dues or damage costs.
        </p>
        <p>
          We may revise pricing with prior notice. Late payment may result in suspension of
          access until dues are cleared.
        </p>
      </LegalSection>

      <LegalSection title="4. Cancellation and Refunds">
        <LegalList
          items={[
            "Monthly memberships may be cancelled by giving written notice before the start of the next billing cycle.",
            "Fees already paid for a current billing period are non-refundable, except where required by law.",
            "Day passes are non-refundable but may be rescheduled once, subject to availability.",
            "Meeting-room bookings cancelled less than 24 hours in advance may be charged in full.",
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Use of the Workspace">
        <p>
          Our centers are shared professional environments. While using them, you agree to:
        </p>
        <LegalList
          items={[
            "Respect other members — keep noise to a reasonable level and use phone booths or meeting rooms for calls where available.",
            "Use the space only for lawful business purposes, and not as a registered residence or for retail or manufacturing activity without our written consent.",
            "Not sublet, resell, or otherwise commercialise your access to the space.",
            "Take care of furniture, equipment, and shared facilities, and report any damage promptly.",
            "Follow the access, security, and safety rules of the center and its building management.",
            "Not bring hazardous materials, illegal substances, or weapons into any center.",
          ]}
        />
        <p>
          We may suspend or terminate access, without refund, for conduct that endangers,
          harasses, or materially disrupts other members or staff.
        </p>
      </LegalSection>

      <LegalSection title="6. Internet and Equipment">
        <p>
          We provide high-speed internet, power backup, and shared equipment on a
          best-effort basis. Occasional outages or maintenance may occur, and we do not
          guarantee uninterrupted service. You are responsible for securing your own devices
          and data, and for maintaining your own backups.
        </p>
      </LegalSection>

      <LegalSection title="7. Guests and Visitors">
        <p>
          Members may host guests in designated areas after signing them in at reception.
          You remain responsible for your guests' conduct and for any damage they cause.
          Extended guest use may require an additional day pass.
        </p>
      </LegalSection>

      <LegalSection title="8. Personal Property">
        <p>
          You are responsible for your own belongings. Coffice 24x7 is not liable for loss,
          theft, or damage to personal property left in any center, including in lockers,
          storage units, or private cabins.
        </p>
      </LegalSection>

      <LegalSection title="9. Mail and Virtual Office Services">
        <p>
          Where mail handling or a virtual office address is included in your plan, the
          service is limited to receiving and holding business correspondence. We may
          refuse or return items that are oversized, perishable, or unlawful, and we may
          discontinue the service if the address is used for activity we reasonably believe
          to be fraudulent.
        </p>
      </LegalSection>

      <LegalSection title="10. Intellectual Property">
        <p>
          The Coffice 24x7 name, logo, website content, photographs, and designs belong to
          us or our licensors. You may not copy, reproduce, or use them for commercial
          purposes without our written permission. Content you create in our spaces remains
          entirely yours.
        </p>
      </LegalSection>

      <LegalSection title="11. Limitation of Liability">
        <p>
          To the extent permitted by law, Coffice 24x7 is not liable for indirect,
          incidental, or consequential losses, including loss of business, revenue, or data,
          arising from the use of our website or centers. Our total liability for any claim
          is limited to the fees you paid us in the three months before the claim arose.
        </p>
      </LegalSection>

      <LegalSection title="12. Termination">
        <p>
          Either party may end a membership in line with the notice terms of the plan. We
          may terminate access immediately for non-payment, breach of these terms, or
          conduct that puts members, staff, or the premises at risk. On termination, you
          must remove your belongings and return any access cards or keys.
        </p>
      </LegalSection>

      <LegalSection title="13. Changes to These Terms">
        <p>
          We may update these terms from time to time. The current version will always be
          available on this page with a "last updated" date. Continued use of our website or
          centers after an update means you accept the revised terms.
        </p>
      </LegalSection>

      <LegalSection title="14. Governing Law">
        <p>
          These terms are governed by the laws of India. Any dispute arising from them is
          subject to the exclusive jurisdiction of the courts at Pune, Maharashtra.
        </p>
      </LegalSection>

      <LegalSection title="15. Contact Us">
        <p>Questions about these terms? Get in touch:</p>
        <p>
          Coffice 24x7, Pune, Maharashtra, India
          <br />
          Email:{" "}
          <a
            href="mailto:contact@coffice24x7.in"
            className="text-orange-500 font-medium hover:text-orange-600 transition-colors"
          >
            contact@coffice24x7.in
          </a>
          <br />
          Phone:{" "}
          <a
            href="tel:+919179748353"
            className="text-orange-500 font-medium hover:text-orange-600 transition-colors"
          >
            +91 91797 48353
          </a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
